"use client";
import { useEffect, useState, useCallback, useRef } from "react";
import { webSocketService } from "../services/WebSocketService";
import { getQuestionType, QuestionType } from "../api/config";
import { Toast } from "@douyinfe/semi-ui";
import {
  ActiveConversation,
  ConversationStatus,
  getHistory,
  hasActiveConversation,
  HistoryResponse,
} from "@/app/api/message";
import {
  CurrentChatStatus,
  MessageSender,
  MessageStatus,
  MessageType,
  SocketConnectionStatus,
  HeaderType,
  RobotMessageType,
} from "../store/types";
import { createClientUUid, createUuid } from "../utils/string";
import { useMessageStore } from "../store";
import { useInitializeHistory } from "../store/hooks";
import { getSocketTicket, SocketTicket } from "../api/auth";
import { getPageLang } from "../locales";
import { detectOSType, isHandyApp } from "../utils/device";
import Locale from "@/app/locales";
import { HistoryLimit, SOCKET_CONNECT_CHANNEL } from "../constants";
import { getEnvRegion } from "../utils/env";
import { queryWelcomeConfig, queryGuideMenuConfig } from "@/app/api/robot";
import { GuideMenuItemType } from "@/app/types/typeConfig/chatQuestion";
import { useCurrentLocale } from "@/app/lib/LocaleProvider";
import { useChatInit } from "./useChatInit";

interface UrlParams {
  group_id?: string;
  channel?: string;
}

export interface QuestionList {
  id?: string;
  issueTypeName?: string;
  abgId?: string;
}

export interface AIQuestionList {
  content?: string;
  languageCode?: string;
  list: GuideMenuItemType[];
  status: boolean;
}

function checkParamsValid(
  params: UrlParams = {},
  questionList: QuestionList[],
) {
  const { group_id } = params;
  if (!group_id) {
    return false;
  }
  return questionList.find((item) => item.id === group_id);
}

async function getSessionStatus(): Promise<ActiveConversation> {
  const result = await hasActiveConversation();
  return result;
}

let socketParams: Partial<SocketTicket & UrlParams> = {};

function getChannelKey(): SOCKET_CONNECT_CHANNEL {
  if (getEnvRegion().includes("CN")) {
    if (isHandyApp()) {
      return SOCKET_CONNECT_CHANNEL.HANDY_CN;
    }
    return SOCKET_CONNECT_CHANNEL.OTHERS_CN;
  }
  if (isHandyApp()) {
    return SOCKET_CONNECT_CHANNEL.HANDY_OR;
  }
  return SOCKET_CONNECT_CHANNEL.OTHERS_OR;
}

export const initSocket = async (
  questionData: QuestionList,
  initCallback?: (status: SocketConnectionStatus) => void,
) => {
  let socketUrl = process.env.NEXT_PUBLIC_API_HOST as string;
  if (!socketUrl) {
    Toast.error("socket error: socket url is empty");
  }
  socketUrl = socketUrl.replace("https", "wss");
  let initSuccess = false;

  // 使用的时候再生成ticket，否则容易失效
  let ticket = "";
  try {
    ticket = (await getSocketTicket()).ticket || "";
  } catch {}
  if (!ticket) {
    if (typeof initCallback === "function") {
      initCallback(SocketConnectionStatus.ERROR);
    }
    return;
  }
  const channelKey = getChannelKey();
  webSocketService.initConnection(
    `${socketUrl}/ws/cus?ticket=${ticket}&channel=${channelKey}&issueTypeId=${questionData.id}&language=${getPageLang()}&terminal=${detectOSType()}`,
    (status) => {
      if (
        status === SocketConnectionStatus.CONNECTED &&
        typeof initCallback === "function" &&
        !initSuccess
      ) {
        initSuccess = true;
        initCallback(status);
      }
    },
  );
  return webSocketService;
};

const handleSocketPrepare = ({
  questionList,
  sessionInfo,
}: {
  questionList: QuestionType[];
  sessionInfo: ActiveConversation;
}) => {
  // 1. 判断url是否包含问题类型参数
  const config = checkParamsValid(socketParams, questionList);

  // 2. 判断用户是否在线
  if (sessionInfo.hasActiveConversation) {
    // 2.1 如果在线，直接进入
    initSocket({ ...socketParams, id: sessionInfo.issueTypeId });
    // 2.2 如果在线，并且在排队中，则需要推送一条排队信息
    if (sessionInfo.status === ConversationStatus.LineUp) {
      useMessageStore.getState().setCurrentChatStatus(CurrentChatStatus.LineUp);
      webSocketService.sendMessage(
        JSON.stringify({ text: sessionInfo.queueDesc }),
        {
          msgType: MessageType.LineUp,
          noNeedSendToWebsocket: true,
          senderType: MessageSender.SYSTEM,
        },
      );
    } else {
      useMessageStore.getState().setCurrentChatStatus(CurrentChatStatus.Online);
    }
  } else {
    // 如果不在线,判断是否需要选择问题类型
    if (config) {
      initSocket({ ...socketParams, id: socketParams.group_id });
    } else {
      const sendData = { questionList };
      // 需要选择
      webSocketService.sendMessage(JSON.stringify(sendData), {
        msgType: MessageType.QuestionList,
        noNeedSendToWebsocket: true,
        senderType: MessageSender.SYSTEM,
        msgClassName: "emptyBackground",
      });
    }
  }
};

export const useClientInit = (
  initSuccess: () => void,
  searchParams?: URLSearchParams,
  isAuthenticated?: boolean, // 新增登录态参数
  _setAuthenticatedCallback?: (authenticated: boolean) => void, // 新增回调函数用于更新登录态
  shouldInitialize: boolean = true, // 是否应该执行初始化（等待认证检查完成）
) => {
  const [questionListData, setQuestionListData] = useState<QuestionList[]>();
  const [aiQuestionListData, setAIQuestionListData] =
    useState<AIQuestionList>();
  const initializeHistory = useInitializeHistory();
  const locale = useCurrentLocale();
  const {
    updateMessageStatus,
    setLastHistoryMsg,
    setSessionInfo,
    setRobotMessage,
  } = useMessageStore();

  // 使用 useRef 而不是 useState 来避免因状态更新触发重新渲染
  const hasInitializedRef = useRef(false);

  // 使用 useCallback 缓存 initHistory 函数
  const initHistory = useCallback(
    (historyData: HistoryResponse) => {
      // 2. 初始化历史记录
      initializeHistory(historyData.records);
      setLastHistoryMsg({
        hasMore: historyData.hasMore,
        nextCursor: historyData.nextCursor,
      });
    },
    [initializeHistory, setLastHistoryMsg],
  );
  const { checkCanService, checkCanServiceInfo } = useChatInit();
  const checkCanServiceInfoRef = useRef(checkCanServiceInfo);
  useEffect(() => {
    checkCanServiceInfoRef.current = checkCanServiceInfo;
  }, [checkCanServiceInfo]);
  // 使用 useCallback 缓存 AIInit 函数
  const AIInit = useCallback(async () => {
    // 不管有没有登录态，都需要获取：1. 欢迎语 2. FAQ 引导菜单列表
    try {
      const languageCode = locale;

      const [welcomeResult, guideMenuResult] = await Promise.allSettled([
        queryWelcomeConfig({
          languageCode,
          type: 1,
        }),
        queryGuideMenuConfig({
          languageCode,
        }),
      ]);
      // 处理欢迎语
      if (welcomeResult.status === "fulfilled" && welcomeResult.value) {
        const welcomeContent = welcomeResult.value;
        // 添加欢迎消息到消息列表
        setRobotMessage({
          id: `robot-welcome-${Date.now()}`,
          clientId: createUuid(),
          type: MessageType.Text,
          headerType: HeaderType.MESSAGE,
          senderType: MessageSender.ROBOT,
          messageType: RobotMessageType.ROBOT_WELCOME,
          content: welcomeContent,
          timestamp: Date.now(),
          senderName: "智能助手",
          robotData: {},
        });
      } else if (welcomeResult.status === "rejected") {
        console.error("Failed to load welcome config:", welcomeResult.reason);
      }

      // 处理引导菜单（FAQ列表）
      if (guideMenuResult.status === "fulfilled" && guideMenuResult.value) {
        const guideMenuData = guideMenuResult.value;

        // 提取问题列表 - GuideMenuConfigType 有 list 和 content 字段
        if (guideMenuData.list && guideMenuData.list.length > 0) {
          setAIQuestionListData(guideMenuData);

          // 添加推荐问题消息
          setRobotMessage({
            id: `robot-recommend-${Date.now()}`,
            clientId: createUuid(),
            type: MessageType.Text,
            headerType: HeaderType.MESSAGE,
            senderType: MessageSender.ROBOT,
            messageType: RobotMessageType.ROBOT_RECOMMEND_QUESTION,
            content: "常见问题：",
            timestamp: Date.now() + 100, // 稍微延迟，确保在欢迎语之后
            senderName: "智能助手",
            robotData: {
              questions: guideMenuData,
            },
          });
        }
      } else if (guideMenuResult.status === "rejected") {
        console.error(
          "Failed to load guide menu config:",
          guideMenuResult.reason,
        );
      }
    } catch (error) {
      console.error("Error in initConfig:", error);
      Toast.error("加载配置失败，请刷新重试");
    }
  }, [locale, setRobotMessage]);

  // 单独获取历史记录的函数
  const loadHistory = useCallback(async () => {
    try {
      const historyData = await getHistory({
        cursor: "",
        limit: HistoryLimit,
      });
      initHistory(historyData);
    } catch (error) {
      console.error("Failed to load history:", error);
    }
  }, [initHistory]);

  // 单独获取问题列表的函数
  const loadQuestionList = useCallback(async () => {
    try {
      const questionList = await getQuestionType();
      setQuestionListData(questionList);
      return questionList;
    } catch (error) {
      console.error("Failed to load question list:", error);
      throw error;
    }
  }, []);

  // 将 initConfig 提取为 useCallback，明确所有依赖项
  const initConfig = useCallback(async () => {
    // 在 HandyApp 中不关心登录态，正常执行
    const inHandyApp = isHandyApp();

    // 如果不在 HandyApp 中，且没有登录态
    if (!inHandyApp && isAuthenticated === false) {
      await AIInit();
      return;
    }

    // 在 HandyApp 中或有登录态时，查询有无进行中的会话
    const sessionInfo = await getSessionStatus();
    // 如果当前url中有form参数，且等于"im"，则不需要执行AIInit
    const targetParam = searchParams?.get("target") || "";
    // 没有进行中的会话，且不是触发im人工。不执行人工客服初始化
    if (
      !sessionInfo.hasActiveConversation &&
      targetParam.toLowerCase() !== "im"
    ) {
      // 需要获取历史记录
      await loadHistory();
      // 如果是handyApp，不执行AIInit
      if (!inHandyApp) {
        await AIInit();
      }
      return;
    }

    // 开始触发im
    const clientId = createClientUUid(MessageType.Template);

    // 发送加载中
    try {
      webSocketService.sendMessage(
        `{"text":"${Locale.systemMsg.messageLoading}"}`,
        {
          msgType: MessageType.Template,
          noNeedSendToWebsocket: true,
          clientId,
        },
      );
      // 1. 判断是否在会话中，并行获取问题列表和历史记录
      await Promise.all([
        // 获取历史数据
        loadHistory(),
      ]);

      // 需要先判断是否是在客服服务时间
      const canProceed = await checkCanService();
      if (!canProceed) {
        setRobotMessage({
          type: MessageType.RichText,
          headerType: HeaderType.MESSAGE,
          senderType: MessageSender.ROBOT,
          messageType: RobotMessageType.ROBOT_NORMAL_REPLY,
          showFeedFooter: false,
          content: checkCanServiceInfoRef.current?.content || "",
          timestamp: Date.now(),
          senderName: "智能助手",
          robotData: {},
        });
        // 获取当前url，删除target参数
        const url = new URL(window.location.href);
        if (url.searchParams.get("target")) {
          url.searchParams.delete("target");
          window.history.replaceState({}, document.title, url.toString());
        }
        return;
      }

      const [questionList] = await Promise.all([
        // 获取问题类型数据
        loadQuestionList(),
      ]);

      setSessionInfo(sessionInfo);

      // 隐藏加载中
      updateMessageStatus(clientId as string, {
        hide: true,
        status: MessageStatus.Success,
      });

      // 5. 暴露，完成初始化
      if (typeof initSuccess === "function") {
        initSuccess();
      }
      const params = {
        group_id: searchParams?.get("group_id") || "",
        // 当前使用渠道
        channel: searchParams?.get("channel") || "",
      };
      socketParams = {
        ...params,
      };
      handleSocketPrepare({
        questionList,
        sessionInfo,
      });
      // 获取当前url，删除target参数
      const url = new URL(window.location.href);
      if (url.searchParams.get("target")) {
        url.searchParams.delete("target");
        window.history.replaceState({}, document.title, url.toString());
      }
    } catch {
      updateMessageStatus(clientId, {
        content: `{"text":"${Locale.systemMsg.refreshPageTips}"}`,
      });
      // 未知异常不再主动抛出
      // Toast.error(Locale.systemMsg.unknownErrorTips);
    }
  }, [
    isAuthenticated,
    AIInit,
    loadHistory,
    loadQuestionList,
    setSessionInfo,
    updateMessageStatus,
    initSuccess,
    searchParams,
  ]);

  useEffect(() => {
    // 等待认证检查完成后再执行初始化
    if (!shouldInitialize) {
      return;
    }

    // 防止重复初始化
    if (hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;
    initConfig();
  }, [initConfig, shouldInitialize]);

  return {
    questionListData,
    aiQuestionListData,
    // 暴露独立的方法，允许外部单独调用
    loadHistory,
    loadQuestionList,
  };
};
